import React, { useState } from 'react';

import {
    Combobox,
    Flex,
    Input,
    InputBase,
    InputBaseProps,
    InputWrapperProps,
    Loader,
    ScrollArea,
    Text,
    useCombobox,
} from '@mantine/core';

import { useCompanyProfile } from 'hooks/use-company-profile';
import { useCompanyProfiles } from 'hooks/use-company-profiles';

export type ManufacturerInputProps = Omit<InputWrapperProps, 'data'> & {
    value: string;
    name?: string;
    error?: string;
    disableAddManufacturers?: boolean;
    onOptionSubmit: (value: string, query: string, mutate: () => Promise<void>) => Promise<void>;
    onBlur?: () => void;
    inputProps?: InputBaseProps;
    disabled?: boolean;
    showClaimed?: boolean;
};

const ManufacturerInput = ({
    name,
    value,
    error,
    disableAddManufacturers,
    onOptionSubmit,
    onBlur,
    inputProps,
    showClaimed,
    ...props
}: ManufacturerInputProps) => {
    const combobox = useCombobox();

    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');

    const { company: selectedManufacturer } = useCompanyProfile(value ?? null);

    const hasSearched = query.length > 1;

    const {
        companies: manufacturers,
        mutate,
        isLoading,
    } = useCompanyProfiles({
        query,
        limit: 24,
        select: {
            name: true,
            internal: true,
        },
        execute: hasSearched,
    });

    const data = manufacturers
        .map((manufacturer) => ({
            value: manufacturer.id,
            label: manufacturer.name,
            claimed: Boolean(manufacturer.claimedAt),
        }))
        .filter((manufacturer) => {
            return manufacturer.label.toLowerCase().includes(query.toLowerCase());
        });

    const onOptionSubmitWrapper = async (value: string) => {
        setLoading(true);
        combobox.closeDropdown();
        await onOptionSubmit(value, query, mutate);
        setLoading(false);
    };

    return (
        <Input.Wrapper {...props}>
            <Combobox store={combobox} onOptionSubmit={onOptionSubmitWrapper} disabled={props.disabled}>
                <Combobox.Target>
                    <InputBase
                        name={name}
                        value={combobox.dropdownOpened ? query : selectedManufacturer?.name || ''}
                        leftSection={loading ? <Loader size="14" /> : null}
                        rightSection={<Combobox.Chevron />}
                        rightSectionPointerEvents="none"
                        onChange={(event) => {
                            setQuery(event.currentTarget.value);
                        }}
                        onClick={() => {
                            combobox.openDropdown();
                        }}
                        onFocus={() => {
                            combobox.openDropdown();
                        }}
                        onBlur={() => {
                            combobox.closeDropdown();
                            onBlur && onBlur();
                        }}
                        placeholder="Search company by name..."
                        disabled={props.disabled}
                        {...inputProps}
                    />
                </Combobox.Target>
                <Combobox.Dropdown>
                    <Combobox.Options>
                        <ScrollArea.Autosize type="always" mah={200}>
                            {isLoading && (
                                <Combobox.Option value="$loading">
                                    <Loader size="xs" color="gray.5" />
                                </Combobox.Option>
                            )}

                            {hasSearched ? (
                                data.map((option) => (
                                    <Combobox.Option value={option.value} key={option.value}>
                                        <Flex gap="xs">
                                            <Text size="sm">{option.label}</Text>
                                            {showClaimed && option.claimed && (
                                                <Text size="xs" c="green">
                                                    Claimed
                                                </Text>
                                            )}
                                        </Flex>
                                    </Combobox.Option>
                                ))
                            ) : (
                                <Combobox.Empty ta="left" c="gray.5">
                                    Enter at least 2 characters to search
                                </Combobox.Empty>
                            )}

                            {hasSearched && !data.length && !isLoading && (
                                <>
                                    {disableAddManufacturers && <Combobox.Empty>Nothing found</Combobox.Empty>}
                                    {!disableAddManufacturers && query && (
                                        <Combobox.Option value="$new">
                                            <Text size="sm">+ Add {query}</Text>
                                        </Combobox.Option>
                                    )}
                                </>
                            )}
                        </ScrollArea.Autosize>
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
            {error && <Input.Error mt={5}>{error}</Input.Error>}
        </Input.Wrapper>
    );
};

export { ManufacturerInput };
